import { useCallback, useEffect, useState, useRef } from "react";

import { focusNextInputElementInJrEditMode, isEmptyString } from "./util";

// Input field for the attribute card
function AttributeInput({ index, value, onChange, delay = 500, Dyn }) {
    const [internalValue, setInternalValue] = useState(value || "");

    // To focus the first element
    const inputRef = useRef(null);
    useEffect(() => {
        if (index === 0) {
            inputRef.current?.focus();
        }
    }, [index]);

    // Sync internal state with external value
    useEffect(() => {
        setInternalValue(value);
    }, [value]);

    // call onchange after a dealy
    useEffect(() => {
        const handler = setTimeout(() => {
            if (!isEmptyString(internalValue)) {
                onChange(index, internalValue);
            }
        }, delay);

        return () => clearTimeout(handler);
    }, [internalValue, delay, onChange, index]);

    const handleKeyDown = (e) => {
        // Focus next element
        if (e.key === "Enter") {
            e.preventDefault();
            focusNextInputElementInJrEditMode(Dyn, e.target);
        }
    };

    return (
        <div className="tde-row">
            <input
                ref={inputRef}
                type="text"
                className="tde-input tde-input-text"
                placeholder={`<attribute${index + 1}>`}
                value={internalValue}
                onChange={(e) => setInternalValue(e.target.value)}
                onBlur={(e) => {
                    if (isEmptyString(e.target.value)) {
                        onChange(index, e.target.value);
                    }
                }}
                onKeyDown={handleKeyDown}
                style={{ height: 42, width: "100%" }}
            ></input>
        </div>
    );
}

// Get attributes from the dataset
function _getAttributes(Dyn) {
    // NOTE: when editing, ignore Attribute.isVisible flag, ignore CaseFilter.caseIsFiltered,
    //  hide attribute with Attribute.isCaseAttribute; compare to CaseTableComponent.getBaseTable()
    const editing = true; // get attributes for editing; compare to CaseTableComponent.getBaseTable()
    const showAttribute = (attr) =>
        (attr.isVisible || editing) && !attr.isCaseAttribute;
    return Dyn.dataSet.attribs
        .filter((attr) => showAttribute(attr))
        .map((attr) => attr.name);
}

// Wrapper for Attribute Card React Element
function AttributeCard({ Dyn }) {
    const numberOfAttributes = 5;
    const [attributes, setAttributes] = useState(_getAttributes(Dyn));

    const onValChange = useCallback(
        (index, value) => {
            const attribs = _getAttributes(Dyn);
            const oldValue = attribs[index];
            // Update only when if old or new value exists
            if (!isEmptyString(oldValue) || !isEmptyString(value)) {
                // Force update default attribute name (attribute1, attribute2, etc.) when value is empty
                const forceUpdateAttributes =
                    !value && attribs[index] === `attribute${index + 1}`;
                if (forceUpdateAttributes) {
                    // Update the attribute value
                    attribs[index] = value;
                    setAttributes(attribs);
                }
                // Update dataset
                setTimeout(() => {
                    const row = 0;
                    const col = index + 1;
                    const constructedData = [[row, col, oldValue, value]];
                    // Update dataset with the new attributes
                    Dyn.dataSet.updateCaseTableValues(constructedData, true);
                });
            }
        },
        [Dyn]
    );

    // Listen for events
    useEffect(() => {
        const subscriptions = [];
        Dyn.mediatorLocalSubscribe(
            subscriptions,
            "records:setAttributeValues",
            (data) => {
                setAttributes(_getAttributes(Dyn));
            }
        );
        return () => {
            Dyn.mediatorLocalRemoveAll(subscriptions);
        };
    }, [Dyn]);
    return (
        <div
            className="tde-elem-attribute-editor"
            style={{
                height: AttributeCard.height,
                width: AttributeCard.width,
            }}
        >
            <h2 className="tde-p-2 tde-m-1 ">Attribute Card</h2>
            <div className="tde-elem-attribute-editor-body">
                {Array.from({ length: numberOfAttributes }, (v, i) => (
                    <AttributeInput
                        key={i}
                        value={attributes[i]}
                        index={i}
                        onChange={onValChange}
                        Dyn={Dyn}
                    />
                ))}
            </div>
        </div>
    );
}
AttributeCard.width = 240;
AttributeCard.height = 380;
AttributeCard.MAX_ATTRIBUTES = 5;

export default AttributeCard;
